import { ApiError, formatPhone, type PhoneChangeResult, type PhoneSettings, type User } from "@rhema/client";
import { Button, Card, SettingRow, SettingsGroup, TextField } from "@rhema/ui";
import { type FormEvent, useEffect, useState } from "react";
import { useCountdown } from "../screens/PhoneSignIn";
import { useApi, useMe, useSession } from "../session/session";
import { PhoneField, type PhoneValue, phoneOf } from "./PhoneField";
import { deviceRegion } from "./useContactSync";

const OFFLINE = "Rhema could not reach the server. Check your connection and try again.";
export const COMBINED = "We found another Rhema account with this number and combined it with this one.";

function messageOf(e: unknown): string {
  return e instanceof ApiError ? e.detail : OFFLINE;
}

function Alert({ children }: { children: string }) {
  return (
    <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
      {children}
    </p>
  );
}

type Sent = { phone: string; startToken: string; resendAfter: number; sentAt: number };

/** Adding, changing and removing your number. The code goes to the new number. */
function NumberSettings({ onChanged }: { onChanged: (phone: string | null) => void }) {
  const api = useApi();
  const me = useMe();
  const { setMe, restart, setNotice } = useSession();
  const [stage, setStage] = useState<"idle" | "number" | "code" | "pin" | "remove">("idle");
  const [pinToken, setPinToken] = useState<string | null>(null);
  const [pin, setPin] = useState("");
  const [value, setValue] = useState<PhoneValue>(() => ({ region: deviceRegion(), number: "" }));
  const [sent, setSent] = useState<Sent | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const left = useCountdown(sent?.resendAfter ?? 0, sent?.sentAt ?? 0);

  const close = () => {
    setStage("idle");
    setSent(null);
    setCode("");
    setPin("");
    setPinToken(null);
    setError(null);
  };

  const run = async (job: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await job();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const send = (phone: string) =>
    run(async () => {
      const res = await api.phoneChangeStart(phone);
      setSent({
        phone,
        startToken: res.start_token,
        resendAfter: res.resend_after ?? 30,
        sentAt: Date.now(),
      });
      setCode("");
      setStage("code");
    });

  const submitNumber = (e: FormEvent) => {
    e.preventDefault();
    const phone = phoneOf(value);
    if (!phone) {
      setError("Check the number. It does not look like a phone number for the country you picked.");
      return;
    }
    void send(phone);
  };

  // A number that already signs in to another Rhema account proves it the same way, with that
  // account's PIN if it has one, and the two accounts become one. The older one is kept, so the
  // session may now be a different account, and the app starts again as it.
  const done = (result: PhoneChangeResult) => {
    if (result.pin_required) {
      setPinToken(result.pin_token);
      setPin("");
      setStage("pin");
      return;
    }
    const { merged, ...user } = result as User & { merged?: boolean };
    if (merged) setNotice(COMBINED);
    onChanged(user.phone ?? null);
    close();
    if (user.id !== me.id) restart();
    else setMe(user);
  };

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    if (!sent) return;
    void run(async () => done(await api.phoneChangeVerify(sent.startToken, code)));
  };

  const submitPin = (e: FormEvent) => {
    e.preventDefault();
    if (!pinToken) return;
    void run(async () => done(await api.phoneChangePin(pinToken, pin)));
  };

  const remove = () =>
    run(async () => {
      const user = await api.removePhone();
      setMe(user);
      onChanged(null);
      close();
    });

  return (
    <SettingsGroup>
      <SettingRow
        title="Phone number"
        detail={me.phone ? formatPhone(me.phone) : "Add your number so people who have it can find you."}
        trailing={
          stage === "idle" && (
            <span className="flex gap-1.5">
              <Button variant="outline" size="sm" onClick={() => setStage("number")}>
                {me.phone ? "Change" : "Add"}
              </Button>
              {me.phone && (
                <Button variant="ghost" size="sm" onClick={() => setStage("remove")}>
                  Remove
                </Button>
              )}
            </span>
          )
        }
      />
      {stage === "number" && (
        <form onSubmit={submitNumber} aria-label="Your new number" className="flex flex-col gap-3 px-4 pb-4">
          <PhoneField
            label={me.phone ? "New number" : "Your number"}
            value={value}
            onChange={setValue}
            disabled={busy}
            autoFocus
          />
          {error && <Alert>{error}</Alert>}
          <p className="m-0 text-[12.5px] text-ink-secondary">
            We send a 6-digit code by text message to this number.
          </p>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || !value.number.trim()}>
              {busy ? "Sending the code" : "Send code"}
            </Button>
            <Button variant="ghost" onClick={close} disabled={busy}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {stage === "code" && sent && (
        <form
          onSubmit={submitCode}
          aria-label="Confirm your new number"
          className="flex flex-col gap-3 px-4 pb-4"
        >
          <p className="m-0 text-[13px] text-ink-secondary">
            We sent a 6-digit code to {formatPhone(sent.phone)}.
          </p>
          <TextField
            label="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          />
          {error && <Alert>{error}</Alert>}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={busy || code.length !== 6}>
              {busy ? "Checking the code" : "Confirm"}
            </Button>
            <Button variant="outline" disabled={busy || left > 0} onClick={() => void send(sent.phone)}>
              {left > 0 ? `Send a new code in ${left} s` : "Send a new code"}
            </Button>
            <Button variant="ghost" onClick={close} disabled={busy}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {stage === "pin" && (
        <form onSubmit={submitPin} aria-label="Two-step PIN" className="flex flex-col gap-3 px-4 pb-4">
          <p className="m-0 text-[13px] leading-normal text-ink-secondary">
            This number has two-step verification. Enter its 6-digit PIN.
          </p>
          <TextField
            label="6-digit PIN"
            type="password"
            inputMode="numeric"
            autoComplete="current-password"
            maxLength={6}
            required
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          {error && <Alert>{error}</Alert>}
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || pin.length !== 6}>
              {busy ? "Checking the PIN" : "Confirm"}
            </Button>
            <Button variant="ghost" onClick={close} disabled={busy}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {stage === "remove" && (
        <div className="flex flex-col gap-3 px-4 pb-4">
          <p className="m-0 text-[13px] leading-normal text-ink-secondary">
            People who have your number will no longer find you on Rhema. Your chats stay as they are.
          </p>
          {error && <Alert>{error}</Alert>}
          <div className="flex gap-2">
            <Button variant="danger" onClick={() => void remove()} disabled={busy}>
              {busy ? "Removing" : "Remove number"}
            </Button>
            <Button variant="ghost" onClick={close} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </SettingsGroup>
  );
}

/** Two-step verification: a PIN SBI asks for when your number signs in on a new device. */
function PinSettings({
  settings,
  onSaved,
}: {
  settings: PhoneSettings;
  onSaved: (settings: PhoneSettings) => void;
}) {
  const api = useApi();
  const [stage, setStage] = useState<"idle" | "pin" | "off">("idle");
  const [pin, setPin] = useState("");
  const [again, setAgain] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const on = settings.pin_enabled;

  const close = () => {
    setStage("idle");
    setPin("");
    setAgain("");
    setEmail("");
    setError(null);
  };

  const run = async (job: () => Promise<PhoneSettings>) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await job());
      close();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (pin.length !== 6) return setError("Your PIN must be 6 digits.");
    if (pin !== again) return setError("The two PINs do not match.");
    void run(() => api.setPhonePin(pin, email.trim()));
  };

  let detail = "Add a PIN that Rhema asks for when your number signs in on a new device.";
  if (!settings.phone) detail = "Add your phone number first.";
  else if (on && settings.recovery_email_set)
    detail = "On. A forgotten PIN can be reset from your recovery email.";
  else if (on) detail = "On. There is no recovery email, so a forgotten PIN cannot be reset.";

  return (
    <SettingsGroup>
      <SettingRow
        title="Two-step verification"
        detail={detail}
        trailing={
          stage === "idle" &&
          settings.phone && (
            <span className="flex gap-1.5">
              <Button variant="outline" size="sm" onClick={() => setStage("pin")}>
                {on ? "Change PIN" : "Turn on"}
              </Button>
              {on && (
                <Button variant="ghost" size="sm" onClick={() => setStage("off")}>
                  Turn off
                </Button>
              )}
            </span>
          )
        }
      />
      {stage === "pin" && (
        <form onSubmit={submit} aria-label="Your two-step PIN" className="flex flex-col gap-3 px-4 pb-4">
          <TextField
            label="6-digit PIN"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={6}
            required
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          <TextField
            label="Type it again"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={6}
            required
            value={again}
            onChange={(e) => setAgain(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          <TextField
            label="Recovery email (optional)"
            type="email"
            autoComplete="email"
            hint={
              on && settings.recovery_email_set
                ? "Enter your recovery email again to keep it. Left empty, it is removed."
                : "If you forget your PIN, we email a reset link here. Without one, a forgotten PIN cannot be reset."
            }
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {error && <Alert>{error}</Alert>}
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || pin.length !== 6 || again.length !== 6}>
              {busy ? "Saving" : on ? "Save PIN" : "Turn on"}
            </Button>
            <Button variant="ghost" onClick={close} disabled={busy}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {stage === "off" && (
        <div className="flex flex-col gap-3 px-4 pb-4">
          <p className="m-0 text-[13px] leading-normal text-ink-secondary">
            Without a PIN, anyone who receives a code for your number can sign in to your account on a new
            device.
          </p>
          {error && <Alert>{error}</Alert>}
          <div className="flex gap-2">
            <Button variant="danger" onClick={() => void run(() => api.removePhonePin())} disabled={busy}>
              {busy ? "Turning off" : "Turn off"}
            </Button>
            <Button variant="ghost" onClick={close} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </SettingsGroup>
  );
}

/** Your number and two-step verification, shown in Settings while phone sign-in is on. */
export function PhoneAndPin() {
  const api = useApi();
  const me = useMe();
  const { setMe, notice, setNotice } = useSession();
  const [settings, setSettings] = useState<PhoneSettings | null>(null);
  const [error, setError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: loaded once, then kept in step locally
  useEffect(() => {
    api
      .phoneSettings()
      .then((loaded) => {
        setSettings(loaded);
        // SBI is the source of the number. A change made elsewhere shows up here.
        if (loaded.phone !== me.phone) setMe({ ...me, phone: loaded.phone });
      })
      .catch((e) => setError(messageOf(e)));
  }, [api]);

  return (
    <section aria-labelledby="phone-settings-title" className="flex flex-col gap-2">
      <h2 id="phone-settings-title" className="m-0 px-1 pt-2 text-[15px] font-bold">
        Phone number and PIN
      </h2>
      {notice === COMBINED && (
        <Card className="flex items-start justify-between gap-3 px-4 py-3.5">
          <p role="status" className="m-0 text-sm text-ink">
            {notice}
          </p>
          <Button variant="ghost" size="sm" onClick={() => setNotice(null)}>
            OK
          </Button>
        </Card>
      )}
      <NumberSettings onChanged={(phone) => setSettings((s) => (s ? { ...s, phone } : s))} />
      {error && (
        <Card className="px-4 py-3.5">
          <Alert>{error}</Alert>
        </Card>
      )}
      {!settings && !error && (
        <p className="m-0 px-1 text-sm text-ink-meta">Loading your two-step verification.</p>
      )}
      {settings && <PinSettings settings={settings} onSaved={setSettings} />}
    </section>
  );
}
